import * as React from "react"

import { cn } from "./utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  // Check if className contains overflow-visible to conditionally apply overflow-hidden
  const hasOverflowVisible = className?.includes('overflow-visible')
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border bg-card/80 backdrop-blur-sm text-card-foreground shadow-md transition-all duration-300 hover:shadow-xl hover:shadow-orange-200/30 dark:hover:shadow-orange-900/20 hover:border-primary/40 relative group",
        hasOverflowVisible ? "overflow-visible" : "overflow-hidden",
        "before:absolute before:inset-0 before:bg-gradient-to-br before:from-primary/0 before:via-transparent before:to-accent/0 before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300 before:pointer-events-none before:z-0",
        "after:absolute after:inset-0 after:bg-gradient-to-br after:from-orange-50/0 after:via-transparent after:to-amber-50/0 dark:after:from-orange-950/0 dark:after:via-transparent dark:after:to-amber-950/0 after:opacity-0 hover:after:opacity-50 dark:hover:after:opacity-20 after:transition-opacity after:duration-300 after:pointer-events-none after:z-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }

